<template>
  <div class="cms-contact-messages">
    <cms-page-header
      :title="$t('cms.contactMessages.title')"
      :subtitle="headerSubtitle"
      :show-create="false"
    />

    <cms-filter-bar>
      <cms-filter-group :label="$t('cms.contactMessages.filters.status')">
        <div class="cms-contact-status-pills">
          <button
            v-for="pill in statusPills"
            :key="pill.value"
            type="button"
            class="cms-contact-status-pill"
            :class="{ active: selectedStatus === pill.value }"
            @click="setStatusFilter(pill.value)"
          >
            {{ pill.label }}
          </button>
        </div>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.contactMessages.filters.topic')">
        <select v-model="selectedTopic" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option value="booking">{{ $t('cms.contactMessages.topics.booking') }}</option>
          <option value="trip">{{ $t('cms.contactMessages.topics.trip') }}</option>
          <option value="custom">{{ $t('cms.contactMessages.topics.custom') }}</option>
          <option value="other">{{ $t('cms.contactMessages.topics.other') }}</option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('general.search')">
        <input
          v-model="searchQuery"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.contactMessages.searchPlaceholder')"
        />
      </cms-filter-group>

      <cms-filter-group :label="''">
        <button class="cms-btn cms-btn-secondary" type="button" :disabled="isLoading" @click="loadMessages">
          <v-icon icon="refresh" size="sm" />
          <span>{{ $t('general.refresh') }}</span>
        </button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-contact-messages-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadMessages">{{ $t('general.retry') }}</button>
    </div>

    <div v-if="isLoading" class="cms-contact-messages-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="messages.length === 0"
      :message="$t('cms.contactMessages.list.emptyStateTitle')"
      :hint="$t('cms.contactMessages.list.emptyStateHint')"
    />

    <div v-else class="cms-contact-inbox">
      <aside class="cms-contact-inbox__list" aria-label="Contact messages">
        <button
          v-for="item in messages"
          :key="item.id"
          type="button"
          class="cms-contact-list-item"
          :class="{
            'cms-contact-list-item--active': selectedMessage?.id === item.id,
            'cms-contact-list-item--unread': item.status === 'new',
          }"
          @click="openDetail(item)"
        >
          <span class="cms-contact-list-item__avatar" aria-hidden="true">
            {{ item.initials }}
          </span>
          <span class="cms-contact-list-item__body">
            <span class="cms-contact-list-item__row">
              <span class="cms-contact-list-item__name">{{ item.name }}</span>
              <span class="cms-contact-list-item__date">{{ item.createdAtShort }}</span>
            </span>
            <span class="cms-contact-list-item__email">{{ item.email }}</span>
            <span class="cms-contact-list-item__preview">{{ item.messagePreview }}</span>
            <span class="cms-contact-list-item__meta">
              <span :class="['cms-status-badge', `status-${item.status}`]">
                {{ $t(`cms.contactMessages.statuses.${item.status}`) }}
              </span>
              <span v-if="item.topicLabel" class="cms-contact-list-item__topic">
                {{ item.topicLabel }}
              </span>
              <span class="cms-contact-list-item__locale">{{ item.locale?.toUpperCase() }}</span>
            </span>
          </span>
        </button>
      </aside>

      <main
        class="cms-contact-inbox__detail"
        :class="{ 'cms-contact-inbox__detail--open': selectedMessage }"
      >
        <template v-if="selectedMessage">
          <div class="cms-contact-detail">
            <header class="cms-contact-detail__header">
              <div class="cms-contact-detail__identity">
                <span class="cms-contact-detail__avatar">{{ selectedMessage.initials }}</span>
                <div>
                  <h2 class="cms-contact-detail__name">{{ selectedMessage.name }}</h2>
                  <a :href="`mailto:${selectedMessage.email}`" class="cms-contact-detail__email">
                    {{ selectedMessage.email }}
                  </a>
                </div>
              </div>
              <div class="cms-contact-detail__header-actions">
                <span :class="['cms-status-badge', `status-${selectedMessage.status}`]">
                  {{ $t(`cms.contactMessages.statuses.${selectedMessage.status}`) }}
                </span>
                <button
                  type="button"
                  class="cms-contact-detail__close"
                  :aria-label="$t('general.close')"
                  @click="closeDetail"
                >
                  <v-icon icon="close" size="sm" />
                </button>
              </div>
            </header>

            <dl class="cms-contact-detail__meta">
              <div>
                <dt>{{ $t('cms.contactMessages.reference') }}</dt>
                <dd>#{{ selectedMessage.id }}</dd>
              </div>
              <div>
                <dt>{{ $t('cms.contactMessages.topic') }}</dt>
                <dd>{{ selectedMessage.topicLabel }}</dd>
              </div>
              <div>
                <dt>{{ $t('cms.contactMessages.receivedAt') }}</dt>
                <dd>{{ selectedMessage.createdAtLabel }}</dd>
              </div>
              <div>
                <dt>{{ $t('cms.contactMessages.locale') }}</dt>
                <dd>{{ selectedMessage.locale?.toUpperCase() }}</dd>
              </div>
            </dl>

            <section class="cms-contact-detail__message-section">
              <h3>{{ $t('cms.contactMessages.message') }}</h3>
              <div class="cms-contact-detail__message-box">
                <p>{{ selectedMessage.message }}</p>
              </div>
            </section>

            <footer class="cms-contact-detail__actions">
              <a
                :href="mailtoHref"
                class="cms-btn cms-btn-primary cms-contact-detail__reply"
              >
                <v-icon icon="mail" size="sm" />
                {{ $t('cms.contactMessages.replyViaEmail') }}
              </a>
              <button
                v-if="selectedMessage.status === 'new'"
                type="button"
                class="cms-btn cms-btn-secondary"
                @click="updateStatus(selectedMessage, 'read')"
              >
                {{ $t('cms.contactMessages.markRead') }}
              </button>
              <button
                v-if="selectedMessage.status === 'read'"
                type="button"
                class="cms-btn cms-btn-secondary"
                @click="updateStatus(selectedMessage, 'new')"
              >
                {{ $t('cms.contactMessages.markNew') }}
              </button>
              <button
                v-if="selectedMessage.status !== 'archived'"
                type="button"
                class="cms-btn cms-btn-secondary"
                @click="updateStatus(selectedMessage, 'archived')"
              >
                {{ $t('cms.contactMessages.archive') }}
              </button>
              <button
                v-if="selectedMessage.status === 'archived'"
                type="button"
                class="cms-btn cms-btn-secondary"
                @click="updateStatus(selectedMessage, 'new')"
              >
                {{ $t('cms.contactMessages.restore') }}
              </button>
              <button
                type="button"
                class="cms-btn cms-btn-danger cms-contact-detail__delete"
                :disabled="deletingId === selectedMessage.id"
                @click="deleteMessage(selectedMessage)"
              >
                <v-icon icon="trash" size="sm" />
                {{ $t('general.delete') }}
              </button>
            </footer>
          </div>
        </template>

        <div v-else class="cms-contact-inbox__placeholder">
          <v-icon icon="mail" size="lg" class="cms-contact-inbox__placeholder-icon" />
          <h3>{{ $t('cms.contactMessages.selectTitle') }}</h3>
          <p>{{ $t('cms.contactMessages.selectHint') }}</p>
        </div>
      </main>
    </div>

    <cms-pagination
      v-if="paginationMeta && !isLoading && messages.length > 0"
      :meta="paginationMeta"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsEmptyState,
  CmsPagination,
} from '@/components/cms';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';

interface ContactMessageCard {
  id: number;
  name: string;
  email: string;
  topic: string | null;
  topicLabel: string;
  message: string;
  messagePreview: string;
  locale: string;
  status: 'new' | 'read' | 'archived';
  createdAt: string | null;
  createdAtLabel: string;
  createdAtShort: string;
  initials: string;
}

export default defineComponent({
  name: 'TheCmsContactMessages',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsEmptyState,
    CmsPagination,
    VIcon,
  },
  setup() {
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const messages = ref<ContactMessageCard[]>([]);
    const selectedMessage = ref<ContactMessageCard | null>(null);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const deletingId = ref<number | null>(null);

    const selectedStatus = ref('');
    const selectedTopic = ref('');
    const searchQuery = ref('');

    const currentPage = ref(1);
    const perPage = ref(25);
    const paginationMeta = ref<{
      currentPage: number;
      lastPage: number;
      perPage: number;
      from: number | null;
      to: number | null;
      total: number;
    } | null>(null);

    const statusPills = computed(() => [
      { value: '', label: t('general.all') },
      { value: 'new', label: t('cms.contactMessages.statuses.new') },
      { value: 'read', label: t('cms.contactMessages.statuses.read') },
      { value: 'archived', label: t('cms.contactMessages.statuses.archived') },
    ]);

    const headerSubtitle = computed(() => {
      const base = t('cms.contactMessages.subtitle');
      const total = paginationMeta.value?.total;
      if (total != null && total > 0) {
        return `${base} · ${t('cms.contactMessages.messageCount', { count: total })}`;
      }
      return base;
    });

    const mailReplySubject = computed(() => 'Re: Your message to WAW Travel');

    const mailtoHref = computed(() => {
      if (!selectedMessage.value) return '#';
      const subject = encodeURIComponent(mailReplySubject.value);
      return `mailto:${selectedMessage.value.email}?subject=${subject}`;
    });

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [response?.data?.data, response?.data, response];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }
      return [];
    };

    const topicLabel = (topic: string | null | undefined) => {
      if (!topic) return t('cms.contactMessages.topics.general');
      const key = `cms.contactMessages.topics.${topic}`;
      return t(key);
    };

    const formatDate = (value: string | null, style: 'full' | 'short' = 'full') => {
      if (!value) return '—';
      try {
        const date = new Date(value);
        if (style === 'short') {
          return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        }
        return date.toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        });
      } catch {
        return value;
      }
    };

    const getInitials = (name: string) => {
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '?';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const mapRecord = (raw: any): ContactMessageCard => {
      const message = raw.message || '';
      const preview = message.length > 120 ? `${message.slice(0, 120)}…` : message;
      const name = raw.name || '';
      return {
        id: raw.id,
        name,
        email: raw.email,
        topic: raw.topic,
        topicLabel: topicLabel(raw.topic),
        message,
        messagePreview: preview,
        locale: raw.locale || 'en',
        status: raw.status || 'new',
        createdAt: raw.created_at,
        createdAtLabel: formatDate(raw.created_at, 'full'),
        createdAtShort: formatDate(raw.created_at, 'short'),
        initials: getInitials(name),
      };
    };

    const buildFilters = () => {
      const filters: Record<string, string> = {};
      if (selectedStatus.value) filters.status = selectedStatus.value;
      if (selectedTopic.value) filters.topic = selectedTopic.value;
      if (searchQuery.value.trim()) filters.search = searchQuery.value.trim();
      return filters;
    };

    const loadMessages = async () => {
      if (!agencySlug.value) return;

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const response = await api.contactMessageService.listCmsContactMessages(agencySlug.value, {
          page: currentPage.value,
          perPage: perPage.value,
          filters: buildFilters(),
          order: { key: 'created_at', direction: 'desc' },
        });

        const list = extractListData(response);
        messages.value = list.map(mapRecord);

        const meta = response?.data?.meta || response?.meta;
        if (meta) {
          paginationMeta.value = {
            currentPage: meta.current_page || currentPage.value,
            lastPage: meta.last_page || 1,
            perPage: meta.per_page || perPage.value,
            from: meta.from ?? null,
            to: meta.to ?? null,
            total: meta.total || 0,
          };
        } else {
          paginationMeta.value = null;
        }

        if (selectedMessage.value) {
          const refreshed = messages.value.find((m) => m.id === selectedMessage.value?.id);
          selectedMessage.value = refreshed ?? null;
        }
      } catch (error: any) {
        console.error('Failed to load contact messages', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    const setStatusFilter = (status: string) => {
      selectedStatus.value = status;
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      loadMessages();
    };

    const handlePerPageChange = (newPerPage: number) => {
      perPage.value = newPerPage;
      currentPage.value = 1;
      loadMessages();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedMessage.value) {
        closeDetail();
      }
    };

    onMounted(() => {
      window.addEventListener('keydown', handleEscape);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleEscape);
    });

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          currentPage.value = 1;
          loadMessages();
        }
      },
      { immediate: true },
    );

    watch([selectedStatus, selectedTopic], () => {
      if (!agencySlug.value) return;
      currentPage.value = 1;
      selectedMessage.value = null;
      loadMessages();
    });

    let searchDebounce: ReturnType<typeof setTimeout> | null = null;
    watch(
      () => searchQuery.value,
      () => {
        if (!agencySlug.value) return;
        if (searchDebounce) clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => {
          currentPage.value = 1;
          selectedMessage.value = null;
          loadMessages();
        }, 400);
      },
    );

    const openDetail = async (item: ContactMessageCard) => {
      selectedMessage.value = item;
      if (item.status === 'new' && agencySlug.value) {
        try {
          await api.contactMessageService.updateCmsContactMessageStatus(
            agencySlug.value,
            item.id,
            'read',
          );
          item.status = 'read';
        } catch {
          // Non-blocking
        }
      }
    };

    const closeDetail = () => {
      selectedMessage.value = null;
    };

    const updateStatus = async (item: ContactMessageCard, status: 'new' | 'read' | 'archived') => {
      if (!agencySlug.value) return;
      try {
        await api.contactMessageService.updateCmsContactMessageStatus(
          agencySlug.value,
          item.id,
          status,
        );
        item.status = status;
        toast.success(t('cms.contactMessages.statusUpdateSuccess'));
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      }
    };

    const deleteMessage = async (item: ContactMessageCard) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) return;

      deletingId.value = item.id;
      try {
        await api.contactMessageService.deleteCmsContactMessage(agencySlug.value, item.id);
        messages.value = messages.value.filter((m) => m.id !== item.id);
        if (selectedMessage.value?.id === item.id) closeDetail();
        toast.success(t('cms.contactMessages.deleteSuccess'));
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        deletingId.value = null;
      }
    };

    return {
      messages,
      selectedMessage,
      isLoading,
      errorMessage,
      deletingId,
      selectedStatus,
      selectedTopic,
      searchQuery,
      paginationMeta,
      statusPills,
      headerSubtitle,
      mailtoHref,
      loadMessages,
      setStatusFilter,
      handlePageChange,
      handlePerPageChange,
      openDetail,
      closeDetail,
      updateStatus,
      deleteMessage,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-contact-messages {
  color: $cms-text-primary;
}

.cms-contact-messages-error {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md;
  margin-bottom: $cms-spacing-md;
  background: $cms-danger-light;
  border: 1px solid $cms-danger-border;
  border-radius: $cms-radius-md;
  color: $cms-danger-color;
}

.cms-contact-messages-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: cms-contact-spin 1s linear infinite;
}

.cms-btn-link {
  background: none;
  border: none;
  padding: 0;
  color: $cms-primary-color;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}

.cms-contact-status-pills {
  display: flex;
  flex-wrap: wrap;
  gap: $cms-spacing-xs;
}

.cms-contact-status-pill {
  padding: 6px 12px;
  border: 1px solid $cms-border-color;
  border-radius: 999px;
  background: $cms-background-white;
  font-size: $cms-font-size-sm;
  font-weight: 500;
  color: $cms-text-secondary;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: $cms-border-color-dark;
    color: $cms-text-primary;
  }

  &.active {
    background: $cms-text-primary;
    border-color: $cms-text-primary;
    color: $cms-background-white;
  }
}

.cms-contact-inbox {
  display: grid;
  grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
  min-height: 520px;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-lg;
  overflow: hidden;
  background: $cms-background-white;
  box-shadow: $cms-shadow-sm;

  @include cms-responsive-tablet {
    grid-template-columns: 1fr;
    min-height: auto;
    border: none;
    box-shadow: none;
    background: transparent;
  }
}

.cms-contact-inbox__list {
  display: flex;
  flex-direction: column;
  border-right: 1px solid $cms-border-color;
  max-height: 640px;
  overflow-y: auto;
  background: $cms-background-light;

  @include cms-responsive-tablet {
    border-right: none;
    max-height: none;
    background: transparent;
    gap: $cms-spacing-sm;
  }
}

.cms-contact-list-item {
  display: flex;
  gap: $cms-spacing-md;
  width: 100%;
  padding: $cms-spacing-md;
  border: none;
  border-bottom: 1px solid $cms-border-color;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }

  &--active {
    background: $cms-background-white;
    box-shadow: inset 3px 0 0 $cms-text-primary;
  }

  &--unread .cms-contact-list-item__name {
    font-weight: 700;
  }

  @include cms-responsive-tablet {
    border: 1px solid $cms-border-color;
    border-radius: $cms-radius-md;
    background: $cms-background-white;

    &--active {
      border-color: $cms-text-primary;
      box-shadow: none;
    }
  }
}

.cms-contact-list-item__avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: $cms-text-primary;
  color: $cms-background-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: $cms-font-size-sm;
  font-weight: 700;
}

.cms-contact-list-item__body {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cms-contact-list-item__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: $cms-spacing-sm;
}

.cms-contact-list-item__name {
  font-size: $cms-font-size-base;
  font-weight: 600;
  color: $cms-text-primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cms-contact-list-item__date {
  flex-shrink: 0;
  font-size: $cms-font-size-xs;
  color: $cms-text-muted;
}

.cms-contact-list-item__email {
  font-size: $cms-font-size-xs;
  color: $cms-text-secondary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cms-contact-list-item__preview {
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 2px;
}

.cms-contact-list-item__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: $cms-spacing-xs;
  margin-top: $cms-spacing-xs;
}

.cms-contact-list-item__topic {
  font-size: $cms-font-size-xs;
  font-weight: 500;
  color: $cms-text-secondary;
  padding: 2px 8px;
  background: $cms-background-gray;
  border-radius: $cms-radius-sm;
}

.cms-contact-list-item__locale {
  font-size: $cms-font-size-xs;
  font-weight: 600;
  color: $cms-text-muted;
}

.cms-status-badge {
  padding: 2px 8px;
  border-radius: $cms-radius-sm;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &.status-new {
    background: rgba(230, 18, 117, 0.12);
    color: #c41066;
    border: 1px solid rgba(230, 18, 117, 0.25);
  }

  &.status-read {
    background: $cms-success-light;
    color: $cms-success-dark;
  }

  &.status-archived {
    background: rgba(158, 158, 158, 0.15);
    color: $cms-text-muted-dark;
  }
}

.cms-contact-inbox__detail {
  display: flex;
  flex-direction: column;
  min-height: 520px;
  overflow-y: auto;
  background: $cms-background-white;

  @include cms-responsive-tablet {
    display: none;

    &--open {
      display: flex;
      position: fixed;
      inset: 0;
      z-index: 1000;
      padding: $cms-spacing-md;
      background: rgba(0, 0, 0, 0.45);
      align-items: stretch;
      justify-content: center;
    }
  }
}

.cms-contact-detail {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: $cms-spacing-lg;

  @include cms-responsive-tablet {
    background: $cms-background-white;
    border-radius: $cms-radius-lg;
    box-shadow: $cms-shadow-lg;
    max-height: 100%;
    overflow-y: auto;
  }
}

.cms-contact-inbox__placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl;
  text-align: center;
  color: $cms-text-secondary;

  h3 {
    margin: $cms-spacing-md 0 $cms-spacing-xs;
    font-size: $cms-font-size-lg;
    color: $cms-text-primary;
  }

  p {
    margin: 0;
    max-width: 280px;
    font-size: $cms-font-size-sm;
    line-height: 1.5;
  }
}

.cms-contact-inbox__placeholder-icon {
  opacity: 0.35;
}

.cms-contact-detail__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $cms-spacing-md;
  margin-bottom: $cms-spacing-lg;
  padding-bottom: $cms-spacing-md;
  border-bottom: 1px solid $cms-border-color;
}

.cms-contact-detail__identity {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  min-width: 0;
}

.cms-contact-detail__avatar {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: $cms-text-primary;
  color: $cms-background-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: $cms-font-size-base;
  font-weight: 700;
}

.cms-contact-detail__name {
  margin: 0 0 2px;
  font-size: $cms-font-size-xl;
  font-weight: 700;
  color: $cms-text-primary;
}

.cms-contact-detail__email {
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: $cms-text-primary;
  }
}

.cms-contact-detail__header-actions {
  display: flex;
  align-items: center;
  gap: $cms-spacing-sm;
  flex-shrink: 0;
}

.cms-contact-detail__close {
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-sm;
  background: $cms-background-white;
  cursor: pointer;
  color: $cms-text-secondary;

  @include cms-responsive-tablet {
    display: inline-flex;
  }

  &:hover {
    background: $cms-background-gray;
    color: $cms-text-primary;
  }
}

.cms-contact-detail__meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $cms-spacing-md;
  margin: 0 0 $cms-spacing-lg;

  @include cms-responsive-mobile {
    grid-template-columns: 1fr;
  }

  dt {
    margin: 0;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $cms-text-muted;
  }

  dd {
    margin: 4px 0 0;
    font-size: $cms-font-size-sm;
    font-weight: 500;
    color: $cms-text-primary;
  }
}

.cms-contact-detail__message-section {
  flex: 1;

  h3 {
    margin: 0 0 $cms-spacing-sm;
    font-size: $cms-font-size-sm;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $cms-text-muted;
  }
}

.cms-contact-detail__message-box {
  padding: $cms-spacing-md;
  background: $cms-background-light;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-md;

  p {
    margin: 0;
    font-size: $cms-font-size-base;
    line-height: 1.6;
    color: $cms-text-primary;
    white-space: pre-wrap;
  }
}

.cms-contact-detail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: $cms-spacing-sm;
  margin-top: $cms-spacing-lg;
  padding-top: $cms-spacing-md;
  border-top: 1px solid $cms-border-color;
}

.cms-contact-detail__reply {
  display: inline-flex;
  align-items: center;
  gap: $cms-spacing-xs;
}

.cms-contact-detail__delete {
  display: inline-flex;
  align-items: center;
  gap: $cms-spacing-xs;
  margin-left: auto;

  @include cms-responsive-mobile {
    margin-left: 0;
    width: 100%;
    justify-content: center;
  }
}

.cms-btn-danger {
  background: $cms-danger-light;
  color: $cms-danger-solid;
  border: 1px solid $cms-danger-border;

  &:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

@keyframes cms-contact-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
